import Ten99RouteConstants from 'Agent/1099s/Ten99RouteConstants';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import {AgentInformationRouteConstants} from 'Agent/AgentInformation/AgentInformationRouteConstants';
import CommissionRouteConstants from 'Agent/Commissions/CommissionsRouteConstants';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import DashboardCard from 'Core/components/Dashboard/Card';
import 'Core/components/Dashboard/Dashboard.sass';
import {ModalState, showModal} from 'Core/ducks/modal';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect} from 'react';
import {connect, useSelector, useDispatch} from 'react-redux';
import {RouteComponentProps, withRouter, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import RequireScope from 'Shared/components/RequireScope';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {Scope} from 'Shared/constants/Scope';
import {UserRouteConstants} from 'Users/Routes';
import {isAgencyInStatus, selectUserScopes} from 'Users/selectors/userProfileSelectors';
import AgentNewsCard from './AgentNewsCard';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';
import Grid from '@material-ui/core/Grid';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import { changeAnalyticsState, PAGE_ID } from 'Core/ducks/analytics';
import { useMediaQuery } from '@material-ui/core';
import { changeCorrespondenceFilterPopup } from 'Agent/Policies/ducks/policyCorrespondence';
import QuarantineModal from './QuarantineModal';

type AgentDashboardProps = {
  showModal: typeof showModal;
} & ReturnType<typeof mapStateToProps> &
  WithClaimsProps &
  RouteComponentProps;
export const AgentDashboard = (props: AgentDashboardProps) => {
  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);
  const isExternal = props.claims.exclusivityStatus.id === ExclusivityStatus.External;
  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const clickCorrespondence = () => {
    props.history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES());
    dispatch(changeCorrespondenceFilterPopup(true));
  }

  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch(changeAnalyticsState({page: {pageId: PAGE_ID[location.pathname]}}));
    let updateFeature = {...featureVariables, FOOTER_OPEN_DEFAULT: true};
    dispatch(updatefeatureVariables(updateFeature));
  }, []);

  return (
    <>
      <main className="dashboard dashboard-agent">
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <h1 className="dashboard__welcome-message">
              Welcome&nbsp;
              {props.claims.firstName}
            </h1>
          </Grid>

          {props.isTerminatedAgent && (
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <DashboardCard
                image={() => <i className="fa fa-file-invoice-dollar" />}
                title="Tax Documents"
                description="Your association with this agency has been terminated. You can currently only view your commissions and 1099's for this agency."
                linkText="View Documents"
                ariaLabel="View Documents"
                onClickLink={() => {
                  props.history.push(Ten99RouteConstants.TEN99s);
                }}
              />
            </Grid>
          )}
          <RequireScope scopes={props.scopes} scope={Scope.SearchAccounts}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <DashboardCard
                image={() => <i className="fa fa-file-invoice" />}
                title="Accounts"
                description="Quickly Search and View Accounts in Your Book of Business."
                linkText="View Accounts"
                ariaLabel="View Accounts"
                onClickLink={() => {
                  dispatch(
                    changeAnalyticsState({
                      page: analytics.page,
                      ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGND_VIEW_ACCOUNTS_CLICK},
                    })
                  );
                  props.history.push(AccountRouteConstants.ACCOUNTS);
                }}
              />
            </Grid>
          </RequireScope>
          <RequireScope scopes={props.scopes} scope={Scope.SearchPolicies}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <DashboardCard
                image={() => <i className="fa fa-file-alt" />}
                title="Policies"
                description="Quickly Search and View Policies In Your Book of Business."
                linkText="View Policies"
                ariaLabel="View Policies"
                onClickLink={() => {
                  dispatch(
                    changeAnalyticsState({
                      page: analytics.page,
                      ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.agentDashViewPoliciesClick},
                    })
                  );
                  props.history.push(PolicyRouteConstants.POLICIES);
                }}
              />
            </Grid>
          </RequireScope>
          <RequireScope scopes={props.scopes} scope={Scope.SearchCommissions}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <DashboardCard
                image={() => <i className="fa fa-money-check-alt" />}
                title="Commissions"
                description="View and Download Earned and Advanced Commission Statements."
                linkText="View Commissions"
                ariaLabel="View Commissions"
                onClickLink={() => {
                  dispatch(
                    changeAnalyticsState({
                      page: analytics.page,
                      ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGND_VIEW_COMMISSIONS_CLICK},
                    })
                  );
                  props.history.push(CommissionRouteConstants.COMMISSIONS);
                }}
              />
            </Grid>
          </RequireScope>
          <RequireScope scopes={props.scopes} scope={Scope.SearchPolicyCorrespondence}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <DashboardCard
                image={() => <i className="fa fa-envelope-open-text" />}
                title="Correspondence"
                description="Review and Download Correspondence Related To Your Policies."
                linkText="View Correspondence"
                ariaLabel="View Correspondence"
                onClickLink={()=>{ isDesktop ?
                  (dispatch(
                    showModal(
                      ModalState.CorrespondenceFilterModal()
                    )
                  )) : clickCorrespondence()
                }}
              />
            </Grid>
          </RequireScope>
          <RequireScope scopes={props.scopes} scope={Scope.SsoAccess}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <DashboardCard
                image={() => <i className="fa fa-link" />}
                title="Single Sign-On"
                description="Easily sign in to legacy applications such as EnrollMyWay."
                linkText="View Legacy Accounts"
                ariaLabel="View Legacy Accounts"
                onClickLink={() => {
                  dispatch(
                    changeAnalyticsState({
                      page: analytics.page,
                      ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGND_VIEW_LEGACY_ACCOUNTS_CLICK},
                    })
                  );
                  props.history.push(UserRouteConstants.SSO_ACCOUNTS);
                }}
              />
            </Grid>
          </RequireScope>
          {selectedAgency && O.isSome(selectedAgency) && (
            <RequireScope scopes={props.scopes} scope={Scope.ViewAgentNews}>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <AgentNewsCard
                  image={() => <i className="fa fa-newspaper" />}
                  linkText="Read Latest News"
                  onClickLink={() => {
                    dispatch(
                      changeAnalyticsState({
                        page: analytics.page,
                        ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGND_VIEW_READ_LATEST_NEWS_CLICK},
                      })
                    );
                    props.history.push(AgentInformationRouteConstants.AGENT_NEWS);
                  }}
                />
              </Grid>
            </RequireScope>
          )}
        </Grid>
      </main>
      <QuarantineModal />
    </>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  scopes: selectUserScopes(state),
  isTerminatedAgent: isAgencyInStatus(state, AgentNumberStatusId.Terminated),
});

export default withUserClaims(
  withRouter(
    connect(mapStateToProps, {
      showModal,
    })(AgentDashboard)
  )
);
